import type { MensagemDTO } from '@viniko/shared';
import { Check, CheckCheck, CircleAlert, Clock, Download, FileText, Reply } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { useCorAtendente } from '../../lib/tema';
import { horaDe } from '../../lib/tempo';
import { PlayerAudio } from './PlayerAudio';
import { TextoWhatsapp, semFormatacao } from '../../lib/formatacao';

const STATUS_ICONE = {
  pendente: <Clock aria-label="Enviando" />,
  enviada: <Check aria-label="Enviada" />,
  entregue: <CheckCheck aria-label="Entregue" />,
  lida: <CheckCheck aria-label="Lida" className="lida" />,
  erro: <CircleAlert aria-label="Erro no envio" className="erro" />,
};

interface Props {
  m: MensagemDTO;
  citada?: MensagemDTO;
  podeResponder: boolean;
  aoResponder: () => void;
}

export function Balao({ m, citada, podeResponder, aoResponder }: Props) {
  const cor = useCorAtendente(m.atendente?.cor);

  if (m.autor === 'sistema') {
    return (
      <div className="sistema">
        <span>{m.conteudo}</span>
        <time>{horaDe(m.enviadoEm)}</time>
      </div>
    );
  }

  const saida = m.autor !== 'cliente';
  const estilo: CSSProperties | undefined =
    m.autor === 'atendente'
      ? ({ '--balao': cor.fundo, '--balao-nome': cor.texto } as CSSProperties)
      : undefined;
  const soMidia = (m.tipo === 'imagem' || m.tipo === 'video') && !m.conteudo;

  return (
    <div id={`msg-${m.id}`} className={`balao-linha ${saida ? 'saida' : 'entrada'}`}>
      <div className={`balao ${m.autor} tipo-${m.tipo} entra`} style={estilo}>
        {m.autor === 'ia' && <span className="balao-nome ia">IA</span>}
        {m.autor === 'atendente' && <span className="balao-nome">{m.atendente?.nomeExibicao}</span>}
        {m.autor === 'celular' && <span className="balao-nome">Enviada pelo celular</span>}
        {citada && (
          <div className="citacao">
            {semFormatacao(citada.conteudo) || citada.midiaNome || 'Mídia'}
          </div>
        )}
        <Midia m={m} />
        {m.conteudo && (
          <span className="balao-texto">
            <TextoWhatsapp texto={m.conteudo} />
          </span>
        )}
        <span className={`balao-meta ${soMidia ? 'sobre-midia' : ''}`}>
          {horaDe(m.enviadoEm)}
          {saida && STATUS_ICONE[m.statusEntrega]}
        </span>
      </div>
      {podeResponder && (
        <button
          className="icone-btn sem-borda acao-balao"
          aria-label="Responder esta mensagem"
          onClick={aoResponder}
        >
          <Reply />
        </button>
      )}
    </div>
  );
}

function Midia({ m }: { m: MensagemDTO }) {
  const [ampliada, setAmpliada] = useState(false);
  if (!m.midiaUrl) return null;

  if (m.tipo === 'imagem') {
    return (
      <>
        <button className="midia-img" onClick={() => setAmpliada(true)} aria-label="Ampliar imagem">
          <img src={m.midiaUrl} alt={m.midiaNome ?? 'Imagem'} loading="lazy" />
        </button>
        {ampliada && (
          <div
            className="visor"
            role="dialog"
            aria-label="Imagem ampliada"
            onClick={() => setAmpliada(false)}
          >
            <img src={m.midiaUrl} alt={m.midiaNome ?? 'Imagem'} />
            <a className="btn" href={`${m.midiaUrl}?baixar=1`} onClick={(e) => e.stopPropagation()}>
              <Download /> Baixar
            </a>
          </div>
        )}
      </>
    );
  }
  if (m.tipo === 'video')
    return <video className="midia-video" src={m.midiaUrl} controls preload="metadata" />;
  if (m.tipo === 'audio') return <PlayerAudio src={m.midiaUrl} />;

  return (
    <a className="midia-doc" href={`${m.midiaUrl}?baixar=1`} title="Baixar arquivo">
      <FileText aria-hidden />
      <span>
        <b>{m.midiaNome ?? 'Arquivo'}</b>
        <small>{(m.midiaNome?.split('.').pop() ?? '').toUpperCase()}</small>
      </span>
      <Download aria-hidden className="baixar" />
    </a>
  );
}
